// Deterministic PRNG for the exercise engine. A given (config, seed) always
// produces the identical run — required for replaying a failed attempt, author
// preview, and the ambiguity fuzz test (assert "no item is ever ambiguous for
// any seed in a range"). The runner picks a fresh seed per attempt for variety.

/** mulberry32 — tiny, fast, well-distributed 32-bit PRNG. Returns [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A small random source threaded through generation. */
export interface Rng {
  next(): number;
  int(maxExclusive: number): number;
  pick<T>(arr: readonly T[]): T;
  shuffle<T>(arr: readonly T[]): T[];
}

export function makeRng(seed: number): Rng {
  const rand = mulberry32(seed);
  const int = (maxExclusive: number) => Math.floor(rand() * maxExclusive);
  return {
    next: rand,
    int,
    pick<T>(arr: readonly T[]): T {
      if (arr.length === 0) throw new Error('pick() from empty array');
      return arr[int(arr.length)]!;
    },
    shuffle<T>(arr: readonly T[]): T[] {
      const out = arr.slice();
      for (let i = out.length - 1; i > 0; i--) {
        const j = int(i + 1);
        const a = out[i]!;
        const b = out[j]!;
        out[i] = b;
        out[j] = a;
      }
      return out;
    },
  };
}

/** Derive a stable child seed from a parent seed + an index (per-step streams). */
export function childSeed(seed: number, index: number): number {
  // xorshift-mix so adjacent indices don't produce correlated streams.
  let h = (seed ^ (index * 0x9e3779b9)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}
